"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, BadgeCheck, Check, Star } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
const reviews = [{
  quote: "Peak Force Fitness completely overhauled my approach to training. The intensity is unmatched, and the results speak for themselves. I've never felt stronger.",
  author: "Alex R.",
  position: "Elite Athlete",
  purchaseTag: "PROGRAM #PFF-001 / STRENGTH ELITE",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/client_alex.png",
  photo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/transformation_alex.png",
  photoSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/transformation_alex.png-wvc-srcset",
  photoSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/transformation_alex.png-wvc-sizes"
}, {
  quote: "The personalized coaching is on another level. Every session is engineered for maximum output. My body composition has shifted drastically in just 8 weeks.",
  author: "Sarah J.",
  position: "Transformation Client",
  purchaseTag: "PROGRAM #PFF-002 / FAT LOSS PROTOCOL",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/client_sarah.png",
  photo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/transformation_sarah.png",
  photoSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/transformation_sarah.png-wvc-srcset",
  photoSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/transformation_sarah.png-wvc-sizes"
}, {
  quote: "No fluff, just pure performance. The online coaching platform is seamless and the feedback is forensic. This is the gold standard for fitness.",
  author: "Mike D.",
  position: "Performance Enthusiast",
  purchaseTag: "PROGRAM #PFF-003 / HYPERTROPHY LAB",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/client_mike.png",
  photo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/transformation_mike.png",
  photoSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/transformation_mike.png-wvc-srcset",
  photoSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/transformation_mike.png-wvc-sizes"
}];
export default function AthleteFeedback() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);
  return <section data-section-id="1939" id="athlete-feedback" className="bg-background text-foreground py-24 md:py-36">
      <div className="mx-auto max-w-[1320px] px-5 md:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
          {}
          <div className="relative lg:col-span-4">
            <div className="relative">
              <p className="font-mono text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Athlete Feedback</p>

              <h2 className="mt-6 font-default text-4xl font-semibold leading-[0.98] tracking-tight md:text-5xl lg:text-[3.5rem]">
                Engineered,
                <br />
                <span className="relative inline-block">
                  Executed
                  <span className="absolute -bottom-1 left-0 flex h-[3px] w-full items-center bg-primary" aria-hidden="true">
                    <span className="absolute -right-1 h-2 w-2 rounded-lg bg-primary" />
                  </span>
                </span>
              </h2>

              <div className="mt-10 flex items-center gap-3">
                <div className="flex items-center gap-1">
                  {Array.from({
                  length: 5
                }).map((_, i) => <Star key={i} className="size-4 fill-primary text-primary" />)}
                </div>
                <p className="font-mono text-sm tracking-[0.08em] text-muted-foreground">
                  5.0 / 5 &middot; 1,200+ TRANSFORMATIONS
                </p>
              </div>

              <div className="mt-10 hidden items-center gap-3 lg:flex">
                <Button variant="outline" size="icon" onClick={() => api?.scrollPrev()} className="size-11 rounded-lg border-muted-foreground/50 bg-transparent text-foreground transition-colors duration-300 hover:border-primary hover:bg-transparent hover:text-foreground" aria-label="Previous review">
                  <ArrowLeft className="size-5" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => api?.scrollNext()} className="size-11 rounded-lg border-muted-foreground/50 bg-transparent text-foreground transition-colors duration-300 hover:border-primary hover:bg-transparent hover:text-foreground" aria-label="Next review">
                  <ArrowRight className="size-5" />
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <Carousel setApi={setApi} opts={{
            align: "start",
            loop: true
          }} className="w-full">
              <CarouselContent>
                {reviews.map((review, i) => <CarouselItem key={i} data-index={i} className="md:basis-1/2">
                    <div className="flex h-full flex-col rounded-lg border border-border bg-card p-6 shadow-md">
                      <div className="overflow-hidden rounded-lg">
                        <img src={review.photo} data-wvc-srcset={review.photoSrcset} data-wvc-sizes={review.photoSizes} alt={`Transformation photo of ${review.author}`} loading="lazy" decoding="async" className="aspect-square w-full object-cover" />
                      </div>

                      <div className="mt-5 flex items-center gap-2">
                        <Badge className="rounded-lg bg-primary/15 px-2.5 py-1 font-mono text-[0.7rem] font-medium uppercase tracking-wide text-foreground hover:bg-primary/15">
                          <BadgeCheck className="size-3 text-primary" />
                          Verified Result
                        </Badge>
                      </div>

                      <p className="mt-4 flex-1 text-base leading-[1.55] text-card-foreground md:text-lg">
                        &ldquo;{review.quote}&rdquo;
                      </p>

                      <div className="mt-6 border-t border-dashed border-border" aria-hidden="true" />

                      <div className="mt-5 flex items-center gap-3">
                        <SafeHydrate><Avatar className="size-10 rounded-lg">
                          <AvatarImage src={review.avatar} alt={review.author} />
                          <AvatarFallback className="bg-secondary font-mono text-xs text-secondary-foreground">
                            {review.author.charAt(0)}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        <div>
                          <p className="font-default text-sm font-semibold leading-tight text-card-foreground">
                            {review.author}
                          </p>
                          <p className="font-mono text-[0.7rem] uppercase tracking-[0.08em] text-muted-foreground">
                            {review.position}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center gap-1.5 font-mono text-[0.7rem] tracking-[0.08em] text-muted-foreground">
                        <Check className="size-3 text-primary" />
                        {review.purchaseTag}
                      </div>
                    </div>
                  </CarouselItem>)}
              </CarouselContent>
            </Carousel>

            {}
            <div className="mt-8 flex items-center justify-center gap-2 lg:hidden">
              {Array.from({
              length: count
            }).map((_, i) => <Button key={i} data-index={i} onClick={() => api?.scrollTo(i)} aria-label={`Go to review ${i + 1}`} variant="ghost" size="sm" className={current === i ? "h-2 w-6 rounded-lg bg-primary hover:bg-primary transition-all duration-300 p-0" : "h-2 w-2 rounded-lg bg-muted-foreground/40 hover:bg-muted-foreground/60 transition-all duration-300 p-0"} />)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}